import Modal from "react-bootstrap/Modal";
import Form from "react-bootstrap/Form";

import { useForm } from "react-hook-form";

import { zodResolver } from "@hookform/resolvers/zod";

import { 
  Alert, 
  InputField, 
  SelectField,
  Button
} from "@evos/components";

import type { ApolloError } from "@apollo/client";
import type { ReactNode } from "react";
import type { ZodTypeAny } from "zod";

type FormStructure = Array<{
  inputName: string;
  inputType: "text" | "number";
  placeholder?: string;
} | {
  inputName: string;
  inputType: "select";
  selectOptions: string[]
}>

type ChargerCommandModalProps = {
  showModal: boolean;
  setShowModal: (value: React.SetStateAction<boolean>) => void;
  header: string
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  commandResponse: Record<string, any> | undefined | null
  resetMutation: () => void
  isResponseLoading: boolean
  responseError: ApolloError | undefined
  formStructure?: FormStructure
  formSubmitFn: (formData: Record<string, string>) => void
  modalBodyText?: string | ReactNode
  zodArgumentResolver?: ZodTypeAny
};

export default function BasicCommandModal({
  showModal,
  setShowModal,
  header,
  commandResponse,
  resetMutation,
  isResponseLoading,
  responseError,
  formStructure,
  formSubmitFn,
  modalBodyText,
  zodArgumentResolver
}: ChargerCommandModalProps) {

  const {
    register,
    handleSubmit,
    reset, 
    getFieldState,
    formState: { 
      errors,
      isSubmitted
    }
  } = useForm({
    defaultValues: formStructure != null
      ? formStructure.reduce((acc, formEntry) => {
        formEntry.inputType === "select" && formEntry.selectOptions != null
          ? acc[formEntry.inputName] = formEntry.selectOptions[0]
          : acc[formEntry.inputName] = "";
        return acc;
      }, {} as Record<string, string>)
      : undefined,
    resolver: zodArgumentResolver != null 
      ? zodResolver(zodArgumentResolver) 
      : undefined
  });

  return (
    <div id="ChargerCommandModal">
      <Modal 
        show={showModal} 
        onHide={() => {
          setShowModal(false);
          reset();
          if (commandResponse != null) {
            resetMutation();
          }
        }}
      >
        <Modal.Header closeButton>
          <Modal.Title>{ header }</Modal.Title>
        </Modal.Header>
        { formStructure != null
          ? <Form onSubmit={handleSubmit(formSubmitFn)}>
            <Modal.Body>
              { modalBodyText }
              {commandResponse == null || responseError != null
                ? <>
                  <h5 className="mb-3">Command Options:</h5>  
                  { formStructure?.map((formEntry) => (
                    formEntry.inputType === "select"
                      ? <SelectField
                        key={formEntry.inputName}
                        name={formEntry.inputName}
                        label={formEntry.inputName[0].toUpperCase() + formEntry.inputName.slice(1)}
                        options={formEntry.selectOptions.map((option) => ({ value: option, label: option }))}
                        register={register}
                        required
                        error={errors[formEntry.inputName]}
                        isSubmitted={isSubmitted}
                        getFieldState={getFieldState}
                      />
                      : <InputField
                        key={formEntry.inputName}
                        name={formEntry.inputName}
                        label={formEntry.inputName[0].toUpperCase() + formEntry.inputName.slice(1)}
                        type={formEntry.inputType}
                        placeholder={formEntry.placeholder ?? formEntry.inputName}
                        register={register}
                        required
                        error={errors[formEntry.inputName]}
                        isSubmitted={isSubmitted}
                        getFieldState={getFieldState}
                      />
                  ))
                  }
                </>
                : <pre>{JSON.stringify(commandResponse, null, 2)}</pre>
              }
              { responseError != null 
                && <Alert variant="danger" message="There was an error sending the charger command. Please try again." />
              }
            </Modal.Body>
            <Modal.Footer>
              { commandResponse == null
                ? <>
                  <Button 
                    variant="secondary"
                    onClick={() => {
                      setShowModal(false);
                      reset();
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="primary"
                    type="submit"
                    isLoading={isResponseLoading}
                  >
                    Send
                  </Button>
                </>
                : <Button 
                  variant="secondary"
                  onClick={async () => {
                    setShowModal(false);
                    setTimeout(() => {
                      reset();
                      resetMutation();
                    }, 500);
                  }}
                >
                  Close
                </Button>
              }
            </Modal.Footer>
          </Form>
          : <>
            <Modal.Body>
              {commandResponse == null || responseError != null
                ? <p className="mb-0">{modalBodyText ?? "Send Command?"}</p>
                : <pre>{JSON.stringify(commandResponse, null, 2)}</pre>
              }
              { responseError != null 
                && <Alert variant="danger" message="There was an error sending the charger command. Please try again." />
              }
            </Modal.Body>
            <Modal.Footer>
              { commandResponse == null
                ? <>
                  <Button 
                    variant="secondary"
                    onClick={() => setShowModal(false)}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="primary"
                    isLoading={isResponseLoading}
                    onClick={() => formSubmitFn({})}
                  >
                    Send
                  </Button>
                </>
                : <Button 
                  variant="secondary"
                  onClick={async () => {
                    setShowModal(false);
                    setTimeout(() => {
                      reset();
                      resetMutation();
                    }, 500);
                  }}
                >
                  Close
                </Button>
              }
            </Modal.Footer>
          </>
        }
      </Modal>
    </div>
  );
}